import { encodeHtml } from "./skyward.base.extensions";
import { isBusyOrInitializing, isNotEmpty } from "./skyward.base.requestDataHash";
import { isDirty } from "./skyward.ui.changeTracking";
import { getRowDataHash, getValue } from "./skyward.ui.controls";
import { JQueryInput } from "./types";

type FieldValue = { name: string, value: string | number, hash?: string | null };

const skyward = window.skyward;

/**
 * Return an object containing the values of all fields, editable browses, and repeaters within
 * the data object viewer.
 * The field values, keyed by field name, browseid, repeater id, or adapatable repeater data-key.
 * @param container The root of the Data Object Viewer
 */
export async function getFieldValues(container: JQuery<HTMLElement>)
{
    const values = {};

    const namedInputs = container.find("[name]").not(".doNotPost");
    $.extend(values, await getFromItems(namedInputs, getFieldValueFromNamedInput));

    const editableBrowses = container.find(".editablebrowse");
    $.extend(values, await getFromItems(editableBrowses, getFieldValueFromEditableBrowse));

    const repeaters = container.find(".repeater");
    $.extend(values, await getFromItems(repeaters, getFieldValueFromRepeater));

    // Account for htmlViewers (iframes)
    const iframePromises = container.find(".htmlViewer, .framedHTMLViewer").not(".js-ignoreIFrameContents").toArray().map(function (elem)
    {
        const iframe = $(elem);
        return skyward.iframeListener.getFieldValues(iframe);
    });
    const iframeFieldValues = await Promise.all(iframePromises);
    $.extend(values, {}, ...iframeFieldValues);
    return values as any;

    async function getFromItems(items: JQuery, valueFunc: (input: HTMLElement) => Awaited<FieldValue | undefined> | Promise<FieldValue | undefined>)
    {
        const nameValues = await Promise.all($.map(items, valueFunc));
        const values: { [key: string]: any; } = {};
        for (const nameValue of nameValues)
        {
            if (!nameValue)
            {
                continue;
            }
            values[nameValue.name] = nameValue.value;
            const hasValueForHash = isNotEmpty(nameValue.value);
            const hasNonSentinelHash = !isBusyOrInitializing(nameValue.hash);
            if (hasValueForHash && hasNonSentinelHash && nameValue.hash !== null && nameValue.hash !== undefined)
            {
                values[nameValue.name + "_hash"] = nameValue.hash;
            }
        }
        return values;
    }
}

export function getFieldValueFromNamedInput(field: JQueryInput)
{
    const jqField = $(field);

    const name = jqField.attr("name");
    if (!name)
    {
        return;
    }

    const isOldDatepicker = jqField.hasClass("datepicker");
    const isDatePicker = jqField.hasClass("datePicker") || jqField.is("sky-datepicker");
    const isDateTimePicker = jqField.hasClass("dateTimePicker") || jqField.is("sky-datetimepicker");
    const hasNonstandardValues = jqField.hasClass("relativeDate");

    let value = getValue(jqField);
    let hash = getRowDataHash(jqField);

    if (isOldDatepicker && !hasNonstandardValues)
    {
        if (skyward.datepicker.isValidDateObject(value))
        {
            value = $.datepicker.formatDate("mm/dd/yy DD", value);
        }
        else if ((jqField.val() as string).trim() === "")
        {
            value = "";
        }
        else
        {
            value = new Date(NaN).toString();
        }

        // Dates posted to the server that aren't serialized to JSON need to have their format changed.
        // The default date .toString() posts in a format not recognized by C#'s DateTime parse.
        value = encodeHtml(value);
    }
    else if (isDatePicker)
    {
        if (value && (value as Date).isValidDate())
        {
            value = (value as Date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
        }
        else
        {
            value = "";
        }
    }
    else if (isDateTimePicker)
    {
        if (value && (value as Date).isValidDate())
        {
            value = (value as Date)
                .toLocaleString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                })
                .replace(',', ''); // remove the comma between date and time
        }
        else
        {
            value = "";
        }
    }
    else
    {
        value = typeof value === "object" ? JSON.stringify(value) : value;
    }

    hash = Array.isArray(hash) ? hash.join(",") : hash;

    const fieldValue: FieldValue =
    {
        name,
        value,
        hash
    }

    return fieldValue;
}

function getFieldValueFromEditableBrowse(browse: JQueryInput)
{
    const jqBrowse = $(browse);

    const fieldValue: FieldValue =
    {
        name: jqBrowse.attr("id")!,
        value: JSON.stringify(jqBrowse.data("editablebrowsedata"))
    };

    return fieldValue;
}

function getFieldValueFromRepeater(repeater: JQueryInput)
{
    const jqRepeater = $(repeater);

    if (jqRepeater.hasClass("js-dovAdaptableRepeater"))
    {
        return getFieldValueFromAdaptableRepeater(jqRepeater);
    }

    // Legacy Repeater Save
    const fieldValue: FieldValue =
    {
        name: jqRepeater.attr("id")!,
        value: JSON.stringify(jqRepeater.data("savedata"))
    };

    return Promise.resolve(fieldValue);
}

async function getFieldValueFromAdaptableRepeater(adaptableRepeater: JQuery<HTMLElement>)
{
    const jqAdaptableRepeater = $(adaptableRepeater);

    type ItemValue = {
        Data: { [key: string]: any; };
        Key?: string;
    };

    const itemizedValues = await Promise.all(jqAdaptableRepeater.children(".js-dovAdaptableRepeaterItem")
        .filter(function () { return isDirty($(this)); })
        .map(async function ()
        {
            const editableItem = $(this);

            const returnObj: ItemValue = { Data: await getFieldValues(editableItem) };

            const key = editableItem.data("key");
            if (key !== undefined)
            {
                returnObj.Key = key;
            }

            return returnObj;
        })
    );

    const fieldValue: FieldValue =
    {
        name: jqAdaptableRepeater.attr("name")!,
        value: JSON.stringify(itemizedValues)
    };

    return fieldValue;
}

/**
 * Disable the save, cancel, and save-and-back buttons.
 * @param dovId The id of the Data Object Viewer whose buttons to disable
 */
export function disableButtons(dovId: string)
{
    $("#" + dovId + "_save").attr('disabled', 'disabled').removeClass("dirty");
    $("#" + dovId + "_saveBack").attr('disabled', 'disabled').removeClass("dirty");
    $("#" + dovId + "_cancel").attr('disabled', 'disabled').removeClass("dirty");
}

/**
 * Enable the save, cancel, and save-and-back buttons.
 * @param dovId The id of the Data Object Viewer whose buttons to enable
 */
export function enableButtons(dovId: string)
{
    $("#" + dovId + "_save").attr('disabled', null).addClass("dirty");
    $("#" + dovId + "_saveBack").attr('disabled', null).addClass("dirty");
    $("#" + dovId + "_cancel").attr('disabled', null).addClass("dirty");
}